import { useEffect, useState } from 'react';
import { enUS } from 'date-fns/locale/en-US';

/**
 * Month and day names in the app's language, for date-fns (formatInTimeZone(...,
 * { locale })) and for the small calendars. Each language's date-fns locale is its
 * own chunk, loaded when the language is used. Urdu and Marathi have none in
 * date-fns: their names come from the browser (Intl) on top of the English rules.
 */

const LOADERS = {
  ar: () => import('date-fns/locale/ar').then((m) => m.ar),
  bn: () => import('date-fns/locale/bn').then((m) => m.bn),
  de: () => import('date-fns/locale/de').then((m) => m.de),
  es: () => import('date-fns/locale/es').then((m) => m.es),
  fr: () => import('date-fns/locale/fr').then((m) => m.fr),
  hi: () => import('date-fns/locale/hi').then((m) => m.hi),
  id: () => import('date-fns/locale/id').then((m) => m.id),
  it: () => import('date-fns/locale/it').then((m) => m.it),
  ja: () => import('date-fns/locale/ja').then((m) => m.ja),
  ko: () => import('date-fns/locale/ko').then((m) => m.ko),
  pl: () => import('date-fns/locale/pl').then((m) => m.pl),
  pt: () => import('date-fns/locale/pt').then((m) => m.pt),
  ru: () => import('date-fns/locale/ru').then((m) => m.ru),
  sk: () => import('date-fns/locale/sk').then((m) => m.sk),
  ta: () => import('date-fns/locale/ta').then((m) => m.ta),
  te: () => import('date-fns/locale/te').then((m) => m.te),
  tr: () => import('date-fns/locale/tr').then((m) => m.tr),
  vi: () => import('date-fns/locale/vi').then((m) => m.vi),
  zh: () => import('date-fns/locale/zh-CN').then((m) => m.zhCN),
};

const baseLang = (lang) => String(lang || 'en').split('-')[0].toLowerCase();

const WIDTH = { narrow: 'narrow', short: 'short', abbreviated: 'short', wide: 'long' };
/** English rules with the month and day names of `lang` from Intl. */
export const intlLocale = (lang) => {
  const name = (opts, date) => new Intl.DateTimeFormat(lang, { ...opts, timeZone: 'UTC' }).format(date);
  return {
    ...enUS,
    code: lang,
    localize: {
      ...enUS.localize,
      month: (n, o = {}) => name({ month: WIDTH[o.width] || 'long' }, new Date(Date.UTC(2021, n, 15))),
      // 2021-08-01 was a Sunday: day n of the week is the 1st + n.
      day: (n, o = {}) => name({ weekday: WIDTH[o.width] || 'long' }, new Date(Date.UTC(2021, 7, 1 + n))),
    },
  };
};

const cache = { en: enUS };
const listeners = new Set();
let current = enUS;

/** The date-fns locale of the language in use right now (English until it loads). */
export const dateLocale = () => current;

export const loadDateLocale = async (lang) => {
  const code = baseLang(lang);
  if (!cache[code]) {
    try {
      cache[code] = LOADERS[code] ? await LOADERS[code]() : intlLocale(code);
    } catch {
      cache[code] = intlLocale(code);
    }
  }
  current = cache[code];
  listeners.forEach((fn) => fn(current));
  return current;
};

/** The current date-fns locale; re-renders when the language's locale arrives. */
export const useDateLocale = (lang) => {
  const [locale, setLocale] = useState(() => cache[baseLang(lang)] || current);
  useEffect(() => {
    listeners.add(setLocale);
    loadDateLocale(lang).then(setLocale);
    return () => { listeners.delete(setLocale); };
  }, [lang]);
  return locale;
};

/** One letter (or the shortest form) per weekday, starting with `weekStartsOn` (0 = Sunday). */
export const weekdayLetters = (lang, weekStartsOn = 1) => {
  const fmt = new Intl.DateTimeFormat(lang, { weekday: 'narrow', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2021, 7, 1 + ((weekStartsOn + i) % 7)))));
};

/** Short weekday names (Mon, Tue...), starting with `weekStartsOn`. */
export const weekdayShortNames = (lang, weekStartsOn = 1) => {
  const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2021, 7, 1 + ((weekStartsOn + i) % 7)))));
};

/** "October 2026" (standalone month and year) in `lang`. */
export const monthYear = (date, lang) => {
  const s = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(date);
  return s.charAt(0).toLocaleUpperCase(lang) + s.slice(1);
};

/** The twelve month names (standalone) in `lang`. */
export const monthNames = (lang, width = 'long') => {
  const fmt = new Intl.DateTimeFormat(lang, { month: width, timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, n) => {
    const s = fmt.format(new Date(Date.UTC(2021, n, 15)));
    return s.charAt(0).toLocaleUpperCase(lang) + s.slice(1);
  });
};
